/**
 * Sync Queue Manager
 * Handles queuing of failed API operations and background retry attempts
 */

const SYNC_QUEUE_KEY = 'syncQueue';
const SYNC_STATUS_KEY = 'syncStatus';

/**
 * Queue item structure:
 * {
 *   id: string (unique identifier)
 *   type: string (e.g., 'movieSelection', 'genrePreferences')
 *   apiCall: string (serialized function name)
 *   data: object (payload to send)
 *   attempts: number (retry count)
 *   timestamp: number (when it was added)
 *   lastAttempt: number (last retry timestamp)
 * }
 */

/**
 * Get all items in the sync queue
 */
export function getSyncQueue() {
  try {
    const queue = localStorage.getItem(SYNC_QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch (error) {
    console.error('Error reading sync queue:', error);
    return [];
  }
}

/**
 * Save queue to localStorage
 */
function saveSyncQueue(queue) {
  try {
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Error saving sync queue:', error);
  }
}

/**
 * Add an item to the sync queue
 */
export function addToSyncQueue(type, apiCallName, data) {
  const queue = getSyncQueue();
  const id = `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const item = {
    id,
    type,
    apiCall: apiCallName,
    data,
    attempts: 0,
    timestamp: Date.now(),
    lastAttempt: null
  };
  
  queue.push(item);
  saveSyncQueue(queue);
  updateSyncStatus();
  
  console.log(`[QUEUE] Queued for sync: ${type}`);
  return id;
}

/**
 * Remove an item from the sync queue
 */
export function removeFromSyncQueue(id) {
  const queue = getSyncQueue();
  const filtered = queue.filter(item => item.id !== id);
  saveSyncQueue(filtered);
  updateSyncStatus();
  
  console.log(`[QUEUE] Removed from sync queue: ${id}`);
}

/**
 * Update attempt count for a queue item
 */
export function updateQueueItemAttempt(id) {
  const queue = getSyncQueue();
  const item = queue.find(item => item.id === id);
  
  if (item) {
    item.attempts += 1;
    item.lastAttempt = Date.now();
    saveSyncQueue(queue);
  }
}

/**
 * Get sync status for display
 */
export function getSyncStatus() {
  const queue = getSyncQueue();
  return {
    hasPendingItems: queue.length > 0,
    pendingCount: queue.length,
    items: queue.map(item => ({
      type: item.type,
      attempts: item.attempts,
      timestamp: item.timestamp
    }))
  };
}

/**
 * Update sync status in localStorage (for reactive updates)
 */
function updateSyncStatus() {
  const status = getSyncStatus();
  localStorage.setItem(SYNC_STATUS_KEY, JSON.stringify(status));
  
  // Dispatch custom event for components to listen
  window.dispatchEvent(new CustomEvent('syncStatusChanged', { detail: status }));
}

/**
 * Clear all synced items from queue
 */
export function clearSyncQueue() {
  localStorage.removeItem(SYNC_QUEUE_KEY);
  updateSyncStatus();
  console.log('[QUEUE] Sync queue cleared');
}

/**
 * Get pending operations by type
 */
export function getPendingByType(type) {
  const queue = getSyncQueue();
  return queue.filter(item => item.type === type);
}

/**
 * Check if a specific type is pending
 */
export function hasPendingType(type) {
  return getPendingByType(type).length > 0;
}
